import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  connectorApi,
  mappingApi,
  type ConnectorType,
  type ConnectorPayload,
  type FieldMapping,
  type ValidationRule,
  CONNECTOR_TYPES,
  TARGET_ENTITIES,
} from '../api/integrationApi'

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7

const STEPS: { key: string; stepNum: WizardStep }[] = [
  { key: 'connectorSelection', stepNum: 1 },
  { key: 'configuration', stepNum: 2 },
  { key: 'preview', stepNum: 3 },
  { key: 'fieldMapping', stepNum: 4 },
  { key: 'validationRules', stepNum: 5 },
  { key: 'schedule', stepNum: 6 },
  { key: 'review', stepNum: 7 },
]

type ApiConfig = {
  baseUrl: string
  authType: 'none' | 'bearer' | 'apiKey' | 'basic'
  apiKey: string
  headers: string
  timeout: number
  dataPath: string
}

type DbConfig = {
  connectionString: string
  provider: 'postgresql' | 'mssql' | 'mysql'
  query: string
}

type FileConfig = {
  fileName: string
  sheetName: string
  delimiter: string
}

type WebhookConfig = {
  webhookUrl: string
  secret: string
}

export default function IntegrationBuilderPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [step, setStep] = useState<WizardStep>(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1: Connector type
  const [connectorType, setConnectorType] = useState<ConnectorType>(
    (searchParams.get('type') as ConnectorType) || 'api'
  )

  // Step 2: Configuration
  const [connectorName, setConnectorName] = useState('')
  const [connectorDesc, setConnectorDesc] = useState('')
  const [apiConfig, setApiConfig] = useState<ApiConfig>({
    baseUrl: '', authType: 'bearer', apiKey: '', headers: '{}', timeout: 30, dataPath: 'data',
  })
  const [dbConfig, setDbConfig] = useState<DbConfig>({
    connectionString: '', provider: 'postgresql', query: '',
  })
  const [fileConfig, setFileConfig] = useState<FileConfig>({
    fileName: '', sheetName: 'Sheet1', delimiter: ',',
  })
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig>({
    webhookUrl: '', secret: '',
  })

  // Step 3: Preview
  const [previewData, setPreviewData] = useState<Record<string, unknown>[]>([])
  const [sourceFields, setSourceFields] = useState<string[]>([])

  // Step 4: Field mapping
  const [targetEntity, setTargetEntity] = useState('')
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])

  // Step 5: Validation
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([])

  // Step 6: Schedule
  const [mappingName, setMappingName] = useState('')
  const [mappingDesc, setMappingDesc] = useState('')

  // ─── Navigation ───

  const canProceed = (): boolean => {
    switch (step) {
      case 1: return !!connectorType
      case 2: return !!connectorName.trim() && isConfigValid()
      case 3: return previewData.length > 0 || true // Preview is optional
      case 4: return !!targetEntity && fieldMappings.length > 0
      case 5: return true // Validation rules are optional
      case 6: return !!mappingName.trim()
      case 7: return true
      default: return false
    }
  }

  const isConfigValid = (): boolean => {
    switch (connectorType) {
      case 'api': return !!apiConfig.baseUrl.trim()
      case 'database': return !!dbConfig.connectionString.trim()
      case 'file': return !!fileConfig.fileName.trim()
      case 'webhook': return !!webhookConfig.webhookUrl.trim()
      default: return false
    }
  }

  const getConfigJson = (): string => {
    switch (connectorType) {
      case 'api': return JSON.stringify(apiConfig)
      case 'database': return JSON.stringify(dbConfig)
      case 'file': return JSON.stringify(fileConfig)
      case 'webhook': return JSON.stringify(webhookConfig)
      default: return '{}'
    }
  }

  const handleNext = () => {
    if (step < 7) setStep((step + 1) as WizardStep)
  }

  const handlePrev = () => {
    if (step > 1) setStep((step - 1) as WizardStep)
  }

  // Step 3: Generate mock preview data
  useEffect(() => {
    if (step === 3 && previewData.length === 0) {
      // In production: call connector preview endpoint
      const mockFields = ['firstName', 'lastName', 'email', 'department', 'position', 'startDate']
      setSourceFields(mockFields)
      setPreviewData([
        { firstName: 'Ahmet', lastName: 'Yilmaz', email: 'ahmet@company.com', department: 'IT', position: 'Developer', startDate: '2024-01-15' },
        { firstName: 'Fatma', lastName: 'Kaya', email: 'fatma@company.com', department: 'HR', position: 'Manager', startDate: '2023-06-01' },
        { firstName: 'Mehmet', lastName: 'Demir', email: 'mehmet@company.com', department: 'Finance', position: 'Analyst', startDate: '2024-03-20' },
      ])
    }
  }, [step, previewData.length])

  // ─── Save / Finish ───

  const handleFinish = async () => {
    setSaving(true)
    setError(null)
    try {
      // 1. Create connector
      const connectorPayload: ConnectorPayload = {
        name: connectorName.trim(),
        description: connectorDesc.trim() || undefined,
        type: connectorType,
        config: getConfigJson(),
      }
      const connResult = await connectorApi.create(connectorPayload)

      // 2. Create mapping
      await mappingApi.create({
        name: mappingName.trim(),
        description: mappingDesc.trim() || undefined,
        sourceSystem: connectorName.trim(),
        targetEntity,
        connectorId: connResult.id,
        mappings: JSON.stringify(fieldMappings),
        validationRules: validationRules.length > 0 ? JSON.stringify(validationRules) : undefined,
      })

      navigate('/system/integrations')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  // ─── Mapping helpers ───

  const addFieldMapping = () => {
    setFieldMappings([...fieldMappings, { sourceField: '', targetField: '', isRequired: false }])
  }

  const updateFieldMapping = (index: number, field: Partial<FieldMapping>) => {
    const updated = [...fieldMappings]
    updated[index] = { ...updated[index], ...field }
    setFieldMappings(updated)
  }

  const removeFieldMapping = (index: number) => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== index))
  }

  const autoMapFields = () => {
    const targetFields = getTargetFields()
    const autoMapped: FieldMapping[] = sourceFields
      .reduce<FieldMapping[]>((acc, sf) => {
        const match = targetFields.find(
          (tf) => tf.toLowerCase() === sf.toLowerCase() || tf.toLowerCase().includes(sf.toLowerCase())
        )
        if (match) acc.push({ sourceField: sf, targetField: match, isRequired: false })
        return acc
      }, [])
    setFieldMappings(autoMapped)
  }

  const getTargetFields = (): string[] => {
    switch (targetEntity) {
      case 'Employee': return ['firstName', 'lastName', 'email', 'phone', 'departmentId', 'positionId', 'hireDate', 'nationalId', 'birthDate']
      case 'Department': return ['name', 'code', 'description', 'parentId', 'managerId']
      case 'Position': return ['title', 'code', 'description', 'departmentId', 'level']
      case 'Location': return ['name', 'address', 'city', 'country', 'postalCode']
      case 'Organization': return ['name', 'code', 'description', 'type', 'parentId']
      default: return []
    }
  }

  // ─── Validation helpers ───

  const addValidationRule = () => {
    setValidationRules([...validationRules, { field: '', rule: 'required' }])
  }

  const updateValidationRule = (index: number, field: Partial<ValidationRule>) => {
    const updated = [...validationRules]
    updated[index] = { ...updated[index], ...field }
    setValidationRules(updated)
  }

  const removeValidationRule = (index: number) => {
    setValidationRules(validationRules.filter((_, i) => i !== index))
  }

  // ─── Render ───

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
  const cardCls = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6'

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('integrations.builder.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('integrations.builder.subtitle')}
          </p>
        </div>
        <button
          onClick={() => navigate('/system/integrations')}
          className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('integrations.actions.cancel')}
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s) => (
            <button
              key={s.stepNum}
              onClick={() => s.stepNum <= step && setStep(s.stepNum)}
              className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                s.stepNum === step
                  ? 'text-blue-600 dark:text-blue-400'
                  : s.stepNum < step
                    ? 'text-emerald-600 dark:text-emerald-400 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                s.stepNum === step
                  ? 'bg-blue-600 text-white'
                  : s.stepNum < step
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}>
                {s.stepNum < step ? '✓' : s.stepNum}
              </span>
              <span className="hidden md:inline">{t(`integrations.builder.steps.${s.key}`)}</span>
            </button>
          ))}
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((step - 1) / 6) * 100}%` }}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Step 1: Connector Selection */}
      {step === 1 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.connectorSelection')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONNECTOR_TYPES.map((ct) => (
              <button
                key={ct.value}
                onClick={() => setConnectorType(ct.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  connectorType === ct.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-2xl mb-2">
                  {ct.value === 'api' ? '☁️' : ct.value === 'database' ? '🗄️' : ct.value === 'file' ? '📄' : '⚡'}
                </div>
                <div className="font-medium text-gray-900 dark:text-white">{t(ct.labelKey)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(ct.descKey)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Configuration */}
      {step === 2 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.configuration')}
          </h2>
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className={labelCls}>{t('integrations.fields.connectorName')} *</label>
              <input className={inputCls} value={connectorName} onChange={(e) => setConnectorName(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{t('integrations.fields.description')}</label>
              <textarea className={inputCls} rows={2} value={connectorDesc} onChange={(e) => setConnectorDesc(e.target.value)} />
            </div>

            {connectorType === 'api' && (
              <>
                <div>
                  <label className={labelCls}>{t('integrations.fields.baseUrl')} *</label>
                  <input className={inputCls} placeholder="https://api.example.com/v1" value={apiConfig.baseUrl} onChange={(e) => setApiConfig({ ...apiConfig, baseUrl: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>{t('integrations.fields.authType')}</label>
                  <select className={inputCls} value={apiConfig.authType} onChange={(e) => setApiConfig({ ...apiConfig, authType: e.target.value as ApiConfig['authType'] })}>
                    <option value="none">{t('integrations.authTypes.none')}</option>
                    <option value="bearer">{t('integrations.authTypes.bearer')}</option>
                    <option value="apiKey">{t('integrations.authTypes.apiKey')}</option>
                    <option value="basic">{t('integrations.authTypes.basic')}</option>
                  </select>
                </div>
                {apiConfig.authType !== 'none' && (
                  <div>
                    <label className={labelCls}>{t('integrations.fields.apiKey')}</label>
                    <input className={inputCls} type="password" value={apiConfig.apiKey} onChange={(e) => setApiConfig({ ...apiConfig, apiKey: e.target.value })} />
                  </div>
                )}
                <div>
                  <label className={labelCls}>{t('integrations.fields.dataPath')}</label>
                  <input className={inputCls} placeholder="data" value={apiConfig.dataPath} onChange={(e) => setApiConfig({ ...apiConfig, dataPath: e.target.value })} />
                </div>
              </>
            )}

            {connectorType === 'database' && (
              <>
                <div>
                  <label className={labelCls}>{t('integrations.fields.provider')}</label>
                  <select className={inputCls} value={dbConfig.provider} onChange={(e) => setDbConfig({ ...dbConfig, provider: e.target.value as DbConfig['provider'] })}>
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mssql">MSSQL</option>
                    <option value="mysql">MySQL</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t('integrations.fields.connectionString')} *</label>
                  <input className={inputCls} type="password" value={dbConfig.connectionString} onChange={(e) => setDbConfig({ ...dbConfig, connectionString: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>{t('integrations.fields.query')}</label>
                  <textarea className={inputCls} rows={4} placeholder="SELECT * FROM employees" value={dbConfig.query} onChange={(e) => setDbConfig({ ...dbConfig, query: e.target.value })} />
                </div>
              </>
            )}

            {connectorType === 'file' && (
              <>
                <div>
                  <label className={labelCls}>{t('integrations.fields.fileName')} *</label>
                  <input className={inputCls} placeholder="employees.xlsx" value={fileConfig.fileName} onChange={(e) => setFileConfig({ ...fileConfig, fileName: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>{t('integrations.fields.sheetName')}</label>
                  <input className={inputCls} value={fileConfig.sheetName} onChange={(e) => setFileConfig({ ...fileConfig, sheetName: e.target.value })} />
                </div>
              </>
            )}

            {connectorType === 'webhook' && (
              <>
                <div>
                  <label className={labelCls}>{t('integrations.fields.webhookUrl')} *</label>
                  <input className={inputCls} placeholder="https://hooks.example.com/..." value={webhookConfig.webhookUrl} onChange={(e) => setWebhookConfig({ ...webhookConfig, webhookUrl: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>{t('integrations.fields.secret')}</label>
                  <input className={inputCls} type="password" value={webhookConfig.secret} onChange={(e) => setWebhookConfig({ ...webhookConfig, secret: e.target.value })} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.preview')}
          </h2>
          {previewData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {sourceFields.map((f) => (
                      <th key={f} className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-b">{f}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                      {sourceFields.map((f) => (
                        <td key={f} className="px-3 py-2 text-gray-700 dark:text-gray-300">{String(row[f] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-400 mt-2">
                {t('integrations.builder.previewNote', { count: previewData.length })}
              </p>
            </div>
          ) : (
            <p className="text-gray-400">{t('integrations.builder.noPreviewData')}</p>
          )}
        </div>
      )}

      {/* Step 4: Field Mapping */}
      {step === 4 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.fieldMapping')}
          </h2>
          <div className="mb-4">
            <label className={labelCls}>{t('integrations.fields.targetEntity')} *</label>
            <select className={`${inputCls} max-w-xs`} value={targetEntity} onChange={(e) => setTargetEntity(e.target.value)}>
              <option value="">{t('integrations.fields.selectTargetEntity')}</option>
              {TARGET_ENTITIES.map((te) => (
                <option key={te} value={te}>{te}</option>
              ))}
            </select>
          </div>

          {targetEntity && (
            <>
              <div className="flex gap-2 mb-4">
                <button onClick={autoMapFields} className="px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 transition-colors">
                  {t('integrations.builder.autoMap')}
                </button>
                <button onClick={addFieldMapping} className="px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  + {t('integrations.builder.addMapping')}
                </button>
              </div>

              <div className="space-y-2">
                {fieldMappings.map((fm, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <select className={`${inputCls} flex-1`} value={fm.sourceField} onChange={(e) => updateFieldMapping(i, { sourceField: e.target.value })}>
                      <option value="">{t('integrations.builder.sourceField')}</option>
                      {sourceFields.map((sf) => <option key={sf} value={sf}>{sf}</option>)}
                    </select>
                    <span className="text-gray-400">→</span>
                    <select className={`${inputCls} flex-1`} value={fm.targetField} onChange={(e) => updateFieldMapping(i, { targetField: e.target.value })}>
                      <option value="">{t('integrations.builder.targetField')}</option>
                      {getTargetFields().map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input type="checkbox" checked={fm.isRequired} onChange={(e) => updateFieldMapping(i, { isRequired: e.target.checked })} />
                      {t('integrations.builder.required')}
                    </label>
                    <button onClick={() => removeFieldMapping(i)} className="text-red-500 hover:text-red-700 text-sm px-1">✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 5: Validation Rules */}
      {step === 5 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.validationRules')}
          </h2>
          <button onClick={addValidationRule} className="px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors mb-4">
            + {t('integrations.builder.addRule')}
          </button>
          <div className="space-y-2">
            {validationRules.map((vr, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <select className={`${inputCls} flex-1`} value={vr.field} onChange={(e) => updateValidationRule(i, { field: e.target.value })}>
                  <option value="">{t('integrations.builder.selectField')}</option>
                  {fieldMappings.map((fm) => <option key={fm.targetField} value={fm.targetField}>{fm.targetField}</option>)}
                </select>
                <select className={`${inputCls} w-40`} value={vr.rule} onChange={(e) => updateValidationRule(i, { rule: e.target.value as ValidationRule['rule'] })}>
                  <option value="required">{t('integrations.validationTypes.required')}</option>
                  <option value="email">{t('integrations.validationTypes.email')}</option>
                  <option value="date">{t('integrations.validationTypes.date')}</option>
                  <option value="number">{t('integrations.validationTypes.number')}</option>
                  <option value="regex">{t('integrations.validationTypes.regex')}</option>
                </select>
                <button onClick={() => removeValidationRule(i)} className="text-red-500 hover:text-red-700 text-sm px-1">✕</button>
              </div>
            ))}
            {validationRules.length === 0 && (
              <p className="text-sm text-gray-400">{t('integrations.builder.noValidationRules')}</p>
            )}
          </div>
        </div>
      )}

      {/* Step 6: Naming */}
      {step === 6 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.schedule')}
          </h2>
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className={labelCls}>{t('integrations.fields.mappingName')} *</label>
              <input className={inputCls} value={mappingName} onChange={(e) => setMappingName(e.target.value)} placeholder={`${connectorName} - ${targetEntity}`} />
            </div>
            <div>
              <label className={labelCls}>{t('integrations.fields.mappingDescription')}</label>
              <textarea className={inputCls} rows={3} value={mappingDesc} onChange={(e) => setMappingDesc(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* Step 7: Review */}
      {step === 7 && (
        <div className={cardCls}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('integrations.builder.steps.review')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">{t('integrations.review.connector')}</h3>
              <dl className="space-y-1 text-sm">
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.columns.name')}:</dt><dd className="text-gray-900 dark:text-white">{connectorName}</dd></div>
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.columns.type')}:</dt><dd className="text-gray-900 dark:text-white">{connectorType}</dd></div>
              </dl>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">{t('integrations.review.mapping')}</h3>
              <dl className="space-y-1 text-sm">
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.columns.name')}:</dt><dd className="text-gray-900 dark:text-white">{mappingName}</dd></div>
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.columns.targetEntity')}:</dt><dd className="text-gray-900 dark:text-white">{targetEntity}</dd></div>
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.builder.fieldCount')}:</dt><dd className="text-gray-900 dark:text-white">{fieldMappings.length}</dd></div>
                <div className="flex"><dt className="w-32 text-gray-500">{t('integrations.builder.ruleCount')}:</dt><dd className="text-gray-900 dark:text-white">{validationRules.length}</dd></div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={handlePrev}
          disabled={step === 1}
          className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {t('integrations.actions.prev')}
        </button>
        {step < 7 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {t('integrations.actions.next')}
          </button>
        ) : (
          <button
            onClick={handleFinish}
            disabled={saving}
            className="px-6 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {saving ? t('integrations.actions.saving') : t('integrations.actions.finish')}
          </button>
        )}
      </div>
    </div>
  )
}
